/**
 * Config resolution with prototype pollution prevention.
 * Merges user options into defaults, freezes the result.
 */

import type { ReporterConfig, NavigationType, CloudConfig, ReportMode } from '@testrelic/core';
import { isValidConfig, createError, ErrorCode } from '@testrelic/core';
import { discoverConfigFile, parseConfigFile, resolveEnvVars, mergeCloudConfig } from './cloud-config.js';

export const DEFAULT_REDACTION_PATTERNS: (string | RegExp)[] = [
  /AKIA[A-Z0-9]{16}/g,
  /Bearer\s+[A-Za-z0-9\-._~+/]+=*/g,
  /-----BEGIN\s+(RSA\s+)?PRIVATE\sKEY-----[\s\S]*?-----END/g,
  /\/\/[^:]+:[^@]+@/g,
];

export interface ResolvedConfig {
  readonly outputPath: string;
  readonly includeStackTrace: boolean;
  readonly includeCodeSnippets: boolean;
  readonly codeContextLines: number;
  readonly includeNetworkStats: boolean;
  /** Master on/off for Test Navigation capture. Default true. When false the
   *  reporter drops navigation data before upload and logs a one-time notice. */
  readonly captureNavigation: boolean;
  readonly navigationTypes: NavigationType[] | null;
  readonly redactPatterns: (string | RegExp)[];
  readonly testRunId: string | null;
  readonly metadata: Record<string, unknown> | null;
  readonly openReport: boolean;
  readonly htmlReportPath: string;
  readonly includeArtifacts: boolean;
  readonly trackApiCalls: boolean;
  readonly quiet: boolean;
  readonly includeActionSteps: boolean;
  readonly captureConsoleLogs: boolean;
  readonly cloud: CloudConfig | null;
  readonly reportMode: ReportMode;
  readonly streamingThreshold: number;
}

// ---------------------------------------------------------------------------
// API-specific configuration defaults and resolution
// ---------------------------------------------------------------------------

export const DEFAULT_REDACT_HEADERS: readonly string[] = [
  'authorization',
  'cookie',
  'set-cookie',
  'x-api-key',
];

export const DEFAULT_REDACT_BODY_FIELDS: readonly string[] = [
  'password',
  'secret',
  'token',
  'apiKey',
  'api_key',
];

export interface ResolvedApiConfig {
  readonly trackApiCalls: boolean;
  readonly captureRequestHeaders: boolean;
  readonly captureResponseHeaders: boolean;
  readonly captureRequestBody: boolean;
  readonly captureResponseBody: boolean;
  readonly captureAssertions: boolean;
  readonly redactHeaders: readonly string[];
  readonly redactBodyFields: readonly string[];
  readonly apiIncludeUrls: readonly (string | RegExp)[];
  readonly apiExcludeUrls: readonly (string | RegExp)[];
}

export function resolveApiConfig(options?: Partial<ReporterConfig>): ResolvedApiConfig {
  const target = Object.create(null) as Record<string, unknown>;
  target.trackApiCalls = options?.trackApiCalls ?? true;
  target.captureRequestHeaders = options?.captureRequestHeaders ?? true;
  target.captureResponseHeaders = options?.captureResponseHeaders ?? true;
  target.captureRequestBody = options?.captureRequestBody ?? true;
  target.captureResponseBody = options?.captureResponseBody ?? true;
  target.captureAssertions = options?.captureAssertions ?? true;
  target.redactHeaders = options?.redactHeaders ?? [...DEFAULT_REDACT_HEADERS];
  target.redactBodyFields = options?.redactBodyFields ?? [...DEFAULT_REDACT_BODY_FIELDS];
  target.apiIncludeUrls = options?.apiIncludeUrls ?? [];
  target.apiExcludeUrls = options?.apiExcludeUrls ?? [];
  return Object.freeze(target) as unknown as ResolvedApiConfig;
}

export function resolveConfig(options?: Partial<ReporterConfig>): ResolvedConfig {
  if (options !== undefined && !isValidConfig(options)) {
    throw createError(ErrorCode.CONFIG_INVALID, 'Invalid reporter configuration');
  }

  // Prototype pollution prevention: merge onto Object.create(null)
  const target = Object.create(null) as Record<string, unknown>;
  target.outputPath = options?.outputPath ?? './test-results/analytics-timeline.json';
  target.includeStackTrace = options?.includeStackTrace ?? false;
  target.includeCodeSnippets = options?.includeCodeSnippets ?? true;
  target.codeContextLines = options?.codeContextLines ?? 3;
  target.includeNetworkStats = options?.includeNetworkStats ?? true;
  target.captureNavigation = options?.captureNavigation ?? true;
  target.navigationTypes = options?.navigationTypes ?? null;
  target.redactPatterns = [
    ...DEFAULT_REDACTION_PATTERNS,
    ...(options?.redactPatterns ?? []),
  ];
  target.testRunId = options?.testRunId ?? null;
  target.metadata = options?.metadata ?? null;
  const outputPath = target.outputPath as string;
  target.openReport = options?.openReport ?? true;
  target.htmlReportPath = options?.htmlReportPath ?? outputPath.replace(/\.json$/, '.html');
  target.includeArtifacts = options?.includeArtifacts ?? true;
  target.trackApiCalls = options?.trackApiCalls ?? true;
  target.quiet = options?.quiet ?? false;
  target.includeActionSteps = options?.includeActionSteps ?? true;
  target.captureConsoleLogs = options?.captureConsoleLogs ?? true;

  // Cloud config: merge from config file + reporter options + env vars
  const fileConfigPath = discoverConfigFile(process.cwd());
  const fileConfig = fileConfigPath ? parseConfigFile(fileConfigPath) : null;
  const resolvedFileConfig = fileConfig ? resolveEnvVars(fileConfig) : null;
  const cloudConfig = mergeCloudConfig(resolvedFileConfig, options?.cloud);
  target.cloud = cloudConfig.apiKey ? cloudConfig : null;
  target.reportMode = options?.reportMode ?? 'streaming';
  target.streamingThreshold = options?.streamingThreshold ?? 0;

  return Object.freeze(target) as unknown as ResolvedConfig;
}
